// Copyright (c) Docugami, Inc. All rights reserved.

import { useRef, useState } from "react";
import { useCallback } from "react";
import { SearchResult, TextSearch } from "search/TextSearch";
import { getTokenRangeForNormalizedTextIndex, TextRange, TokenRangeForTextRange } from "utils/TokensTextParser";
import { useTokensText } from "./TokensText";

export interface IDocumentSearch {
    createSearchTree(): void;
    isInitialized: boolean;
    queryDocument(searchText: string): SearchResult[];
    getTokensForSearchResults(results: SearchResult[], wordLength: number): TokenRangeForTextRange[];
    clearSearchTree(): void;
}

export function useDocumentSearch(): IDocumentSearch {
    const { isInitialized: tokensTextInitialized, getNormalizedTextForDocument, tokens } = useTokensText();
    const searchTree = useRef<TextSearch | null>();
    const [isInitialized, setIsInitialized] = useState(false);

    const createSearchTree = useCallback(() => {
        if (isInitialized || !tokensTextInitialized) {
            return;
        }

        const targetText = getNormalizedTextForDocument();
        // we can only create a search tree with a nonempty string
        if (!!targetText) {
            searchTree.current = new TextSearch(targetText);
            setIsInitialized(true);
        }
    }, [tokensTextInitialized, getNormalizedTextForDocument, isInitialized])

    const queryDocument = useCallback((searchText: string) => {
        createSearchTree();
        return isInitialized ? searchTree.current!.search(searchText) : [];
    }, [createSearchTree, isInitialized]);

    const clearSearchTree = useCallback(() => {
        searchTree.current = null;
        setIsInitialized(false);
    }, [])

    const getTokensForSearchResults = useCallback((results: SearchResult[], wordLength: number) => {
        if (!tokensTextInitialized || results.length === 0) {
            return [];
        }

        const textRanges = results.map((result): TextRange => {
            return {
                from: result.index,
                to: result.index + wordLength
            }
        });

        return getTokenRangeForNormalizedTextIndex(tokens, textRanges);
    }, [tokensTextInitialized, tokens])

    return {
        createSearchTree: createSearchTree,
        isInitialized: isInitialized,
        queryDocument: queryDocument,
        getTokensForSearchResults: getTokensForSearchResults,
        clearSearchTree: clearSearchTree
    }
}
